import { useStore } from "@nanostores/preact";
import { useEffect, useState } from "preact/hooks";
import type { HotbarSlot, SettingsDoc } from "../net/api";
import { $slotNames, slotView } from "../state/hotbar";
import {
  $cameraPreview, $cameraSettings, $settings, $settingsFailed, $settingsLoaded, $settingsSaving, $unavailable, clearSlot, placeInSlot, saveSettings, swapSlots,
} from "../state/settings";
import { $myAssets, $panel, toast } from "../state/stores";
import { TOOLS } from "../state/tools";
import { Panel } from "./Panel";
import { $touchMode } from "../state/touch";
import { $showFps, setShowFps } from "../state/fps";
import { $gyroOn, GYRO_DENIED, setGyro } from "../state/gyro";
import { askGyro } from "../input/gyro";

/** What every key does (spec 2g §3.3); read-only until key rebinding exists. */
export const KEY_HELP: [string, string][] = [
  ["W A S D", "移動"],
  ["Space", "跳"],
  ["Shift", "衝刺"],
  ["1–9", "快捷列：放置資產或使用工具"],
  ["滾輪", "放置時旋轉"],
  ["E", "移除準星對著的物件"],
  ["E（對著標記）", "開啟標記"],
  ["E（在房間）", "檢查架上的模型／翻頁"],
  ["R", "回房間／回大廳"],
  ["T", "聊天（Tab 切換頻道）"],
  ["M", "信箱"],
  ["F", "加入／退出領地"],
  ["P", "領地面板"],
  ["Tab", "物品欄"],
  ["Esc", "選單／關閉面板"],
];

/** What every touch control does (spec 2p §2.5). */
export const TOUCH_HELP: [string, string][] = [
  ["左半邊按住拖", "移動（推到底跑）"],
  ["右半邊拖曳", "轉視角"],
  ["轉動手機", "轉視角（陀螺儀；沒反應時到設定把它關掉再打開）"],
  ["跳", "跳"],
  ["快捷列", "放置資產或使用工具"],
  ["⟲ ⟳", "放置時旋轉"],
  ["✓ ✕", "確認放置或標記／取消"],
  ["檢查／移除／標記", "準星對著東西時出現"],
  ["加入領地", "加入／退出所在的領地"],
  ["🏠", "回房間／回大廳"],
  ["💬", "聊天（「頻道」切換頻道）"],
  ["✉", "信箱"],
  ["🗺", "領地面板"],
  ["🎒", "物品欄"],
  ["≡", "選單／關閉面板"],
];

type Source = { from: "slot"; index: number } | { from: "item"; item: HotbarSlot };

const sameItem = (a: HotbarSlot, b: HotbarSlot) =>
  a.kind === "asset" ? b.kind === "asset" && a.code === b.code : b.kind === "tool" && a.tool === b.tool;
const sameSource = (a: Source | null, b: Source) =>
  !!a && (a.from === "slot" ? b.from === "slot" && a.index === b.index : b.from === "item" && sameItem(a.item, b.item));

export function SettingsPanel() {
  const loaded = useStore($settingsLoaded);
  const failed = useStore($settingsFailed);
  return (
    <Panel title="設定" onClose={() => $panel.set(null)}>
      {loaded
        ? <><HotbarSection /><ViewSection /></>
        : <p class="hint">{failed ? "設定讀取失敗，請重新整理" : "設定讀取中…"}</p>}
      <DeviceSection />
      <KeyHelp />
    </Panel>
  );
}

function HotbarSection() {
  const doc = useStore($settings);
  const saving = useStore($settingsSaving);
  const assets = useStore($myAssets);
  const names = useStore($slotNames);
  const gone = useStore($unavailable);
  const [picked, setPicked] = useState<Source | null>(null);
  const [dragging, setDragging] = useState<Source | null>(null);

  useEffect(() => {
    if (!picked) return;
    // window capture runs before keys.ts's listener, so Esc cancels the pick instead of closing the panel
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== "Escape") return;
      e.stopImmediatePropagation();
      setPicked(null);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [picked]);

  const put = (target: number, src: Source) => {
    if (src.from === "slot" && src.index === target) return;
    void saveSettings(src.from === "slot" ? swapSlots(doc, src.index, target) : placeInSlot(doc, target, src.item));
  };
  const clickSlot = (i: number) => {
    const me: Source = { from: "slot", index: i };
    if (!picked) { setPicked(me); return; }
    if (!sameSource(picked, me)) put(i, picked);
    setPicked(null);
  };
  const clickItem = (item: HotbarSlot) => {
    const me: Source = { from: "item", item };
    setPicked(sameSource(picked, me) ? null : me);
  };
  const dragFrom = (src: Source) => ({
    draggable: !saving,
    onDragStart: (e: DragEvent) => { e.dataTransfer?.setData("text/plain", ""); setDragging(src); },
    onDragEnd: () => setDragging(null),
  });
  const items = assets.filter((a) => a.status === "ready");
  const tools = Object.entries(TOOLS);
  const itemButton = (item: HotbarSlot, label: string, key: string) => {
    const me: Source = { from: "item", item };
    return (
      <button key={key} class={`secondary settings-item${sameSource(picked, me) ? " picked" : ""}`} aria-pressed={sameSource(picked, me)}
        disabled={saving} onClick={() => clickItem(item)} {...dragFrom(me)}>{label}</button>
    );
  };

  return (
    <section class="settings-hotbar">
      <h3>快捷列</h3>
      <p class="hint">拖曳到格子，或先點一項再點格子；格子之間拖曳或點選會互換。</p>
      <div class="settings-slots">
        {doc.hotbar.map((slot, i) => {
          const me: Source = { from: "slot", index: i };
          const view = slotView(slot, assets, names, gone);
          const cls = `settings-slot${slot ? " filled" : ""}${sameSource(picked, me) ? " picked" : ""}${view.unavailable ? " unavailable" : ""}`;
          return (
            <div class="settings-slot-wrap" key={i}>
              <button class={cls} aria-pressed={sameSource(picked, me)} title={view.text} disabled={saving}
                onClick={() => clickSlot(i)} {...dragFrom(me)}
                onDragOver={(e: DragEvent) => e.preventDefault()}
                onDrop={(e: DragEvent) => { e.preventDefault(); if (dragging) put(i, dragging); setDragging(null); }}>
                <span class="num">{i + 1}</span><span class="label">{view.text}</span>
              </button>
              {slot && <button class="clear" aria-label={`清空第 ${i + 1} 格`} disabled={saving} onClick={() => void saveSettings(clearSlot(doc, i))}>×</button>}
            </div>
          );
        })}
      </div>
      <h4>工具</h4>
      {tools.length === 0
        ? <p class="hint">尚無工具</p>
        : <div class="settings-items">{tools.map(([id, t]) => itemButton({ kind: "tool", tool: id }, t.name, `tool:${id}`))}</div>}
      <h4>我的資產</h4>
      {items.length === 0
        ? <p class="hint">還沒有可用的資產</p>
        : <div class="settings-items">{items.map((a) => itemButton({ kind: "asset", code: a.code }, a.name, a.code))}</div>}
    </section>
  );
}

function ViewSection() {
  const doc = useStore($settings);
  const saving = useStore($settingsSaving);
  const shown = useStore($cameraSettings);
  // closing the panel mid-drag fires no change event; drop the unsaved preview with it
  useEffect(() => () => $cameraPreview.set(null), []);
  const withMouse = (m: Partial<SettingsDoc["mouse"]>): SettingsDoc => ({ ...doc, mouse: { ...doc.mouse, ...m } });
  const value = (e: Event) => Number((e.target as HTMLInputElement).value);
  // saveSettings shows next at once, so the preview can go; a failed save restores the camera with the document
  const commit = (next: SettingsDoc) => { void saveSettings(next); $cameraPreview.set(null); };
  return (
    <section class="settings-view">
      <h3>滑鼠與視野</h3>
      <label>滑鼠靈敏度 {shown.mouse.sensitivity.toFixed(2)}</label>
      <input type="range" name="sensitivity" min="0.25" max="3" step="0.05" value={shown.mouse.sensitivity} disabled={saving}
        onInput={(e) => $cameraPreview.set({ ...$cameraPreview.get(), sensitivity: value(e) })}
        onChange={(e) => commit(withMouse({ sensitivity: value(e) }))} />
      <label>
        <input type="checkbox" name="invertY" checked={doc.mouse.invertY} disabled={saving}
          onChange={(e) => void saveSettings(withMouse({ invertY: (e.target as HTMLInputElement).checked }))} /> 反轉 Y 軸
      </label>
      <label>視野 FOV {shown.fov}°</label>
      <input type="range" name="fov" min="60" max="100" step="1" value={shown.fov} disabled={saving}
        onInput={(e) => $cameraPreview.set({ ...$cameraPreview.get(), fov: value(e) })}
        onChange={(e) => commit({ ...doc, fov: value(e) })} />
    </section>
  );
}

/** Choices kept on this device only, so they work before or without the account's settings. */
function DeviceSection() {
  const fps = useStore($showFps);
  const touch = useStore($touchMode);
  const gyro = useStore($gyroOn);
  // iOS answers a motion permission request only from a tap, and this change event is one
  const switchGyro = (on: boolean) => {
    setGyro(on);
    if (on) void askGyro().then((ok) => { if (!ok) toast(GYRO_DENIED, 6000); });
  };
  return (
    <section class="settings-device">
      <h3>這台裝置</h3>
      {touch && (
        <label>
          <input type="checkbox" name="gyro" checked={gyro}
            onChange={(e) => switchGyro((e.target as HTMLInputElement).checked)} /> 陀螺儀轉視角
        </label>
      )}
      <label>
        <input type="checkbox" name="showFps" checked={fps}
          onChange={(e) => setShowFps((e.target as HTMLInputElement).checked)} /> 顯示幀數
      </label>
    </section>
  );
}

function KeyHelp() {
  const touch = useStore($touchMode);
  return (
    <section class="settings-keys">
      <h3>{touch ? "觸控說明" : "按鍵說明"}</h3>
      <table>
        <tbody>{(touch ? TOUCH_HELP : KEY_HELP).map(([key, what]) => <tr key={key}><th>{key}</th><td>{what}</td></tr>)}</tbody>
      </table>
    </section>
  );
}
